import { useState, useEffect, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { submitOmniauthRequest } from '../services/omniauthRequest';

/**
 * LoginPage - Página de login
 *
 * Migrada para Tailwind CSS.
 * Background: gradiente navy-900 → navy-700 (paleta do manual da marca).
 *
 * Atenção às armadilhas do style.css legado (@media max-width: 812px):
 * - `.login-form { padding: 0 !important }` zerava a lateral do card no mobile
 * - `.login-form button { margin-top: 15px !important }` desalinhava o botão do olho
 * - `.get-pass a { float: left; width: 50% }` era para DOIS links lado a lado
 * Por isso as classes `login-form` e `get-pass` não são usadas aqui — o
 * espaçamento é todo declarado em Tailwind, com o breakpoint do shell (813px).
 */

interface SocialButtonProps {
  icon: string;
  label: string;
  /** Cor da marca do provedor (fundo + hover) */
  brandClasses: string;
  onClick: () => void;
}

/**
 * Botão de login social: ícone em um slot fixo à esquerda separado por
 * um fio de 1px (como no original), rótulo centralizado no botão.
 */
function SocialButton({ icon, label, brandClasses, onClick }: SocialButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`
        relative w-full min-h-[44px]
        rounded-full border-0
        !text-[#ffffff] text-[12px] font-medium
        flex items-center justify-center
        cursor-pointer
        transition-[filter,transform] duration-150 ease-[cubic-bezier(0.23,1,0.32,1)]
        hover:brightness-110 active:scale-[0.98] motion-reduce:transform-none
        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500
        focus-visible:ring-offset-2 focus-visible:ring-offset-navy-800
        ${brandClasses}
      `}
    >
      <span
        aria-hidden="true"
        className="
          absolute left-0 top-[9px] bottom-[9px] w-[46px]
          flex items-center justify-center
          border-0 border-r border-solid border-white/25
          text-[15px]
        "
      >
        <i className={icon}></i>
      </span>
      <span className="px-[54px]">{label}</span>
    </button>
  );
}

export function LoginPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [flash, setFlash] = useState<{ type: string; message: string } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Verifica se há flash message vinda de outra página (ex: recuperar senha) ou erro de OAuth
  useEffect(() => {
    const state = location.state as { flash?: { type: string; message: string } } | null;
    if (state?.flash) {
      setFlash(state.flash);
      // Limpa o state para não mostrar novamente ao recarregar
      window.history.replaceState({}, document.title);
    }

    // Verifica erro de OAuth
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('oauth') === 'error') {
      setError(t('views.devise.sessions.invalid', 'Usuário ou senha inválidos'));
      // Limpa o parâmetro da URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [location]);

  // Adiciona classe page-login ao body para aplicar estilos corretos
  useEffect(() => {
    document.body.className = 'page-login';
    return () => {
      document.body.className = 'fix-header fix-sidebar page-water home';
    };
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    const result = await login(email, password);

    if (result.success) {
      navigate('/');
    } else {
      setError(result.error || t('views.devise.sessions.invalid', 'Usuário ou senha inválidos'));
    }

    setIsSubmitting(false);
  };

  const oauthError = (provider: string) =>
    setError(t('views.login.oauth_start_error', {
      defaultValue: 'Não foi possível iniciar o login com {{provider}}.',
      provider,
    }));

  // Campo de texto (pílula branca) — 44px de altura garante área de toque no mobile
  const fieldWrapperClasses = `
    flex items-center h-[44px]
    bg-white rounded-full overflow-hidden
    focus-within:ring-2 focus-within:ring-sky-500
    focus-within:ring-offset-2 focus-within:ring-offset-navy-800
  `;

  const inputClasses = `
    flex-1 h-full px-[17px]
    border-0 text-[12px] text-navy-800 bg-transparent
    font-['Poppins',sans-serif]
    focus:outline-none placeholder:text-mist-500
  `;

  return (
    <div
      id="main-wrapper"
      className="min-h-screen bg-gradient-to-br from-navy-900 to-navy-700"
    >
      <div className="login">
        <div className="flex flex-wrap mx-0">
          {/* Login content */}
          <div className="login-content mx-auto mt-[20px] min-[813px]:mt-[50px] max-w-[480px] w-full">
            {/* Login form card — a lateral de 20px é o que mantém as pílulas
                afastadas da borda da tela no mobile */}
            <div className="
              px-[20px] pt-[24px] pb-[20px] rounded-[2px]
              min-[813px]:px-[30px] min-[813px]:pt-[30px]
            ">

              {/* Logo */}
              <div className="login-logo text-center mb-[32px] min-[813px]:mb-[80px]">
                <img
                  src="/images/logo_login.png"
                  alt="Laager"
                  width={167}
                  height={153}
                  className="mx-auto w-[150px] min-[813px]:w-[167px]"
                />
              </div>

              {/* Flash message (sucesso/warning) */}
              {flash && (
                <div className={`
                  relative mb-4
                  pl-[60px] pr-12 py-[12px]
                  text-white rounded
                  ${flash.type === 'success'
                    ? '!bg-[#2dcc6f] after:bg-[position:-58px_-166px]'
                    : '!bg-[#f1c40e] after:bg-[position:0_-169px]'
                  }
                  after:content-[''] after:absolute after:w-[57px] after:h-[52px]
                  after:bg-[url('/images/icons-set.png')] after:bg-no-repeat
                  after:top-[14px] after:left-[5px]
                `} role="alert">
                  <small className="text-[80%] font-normal">{flash.message}</small>
                  <button
                    type="button"
                    className="absolute top-[8px] right-[4px] w-[36px] h-[36px] flex items-center justify-center rounded-full text-[20px] leading-none font-normal opacity-60 hover:opacity-90 bg-transparent border-0 cursor-pointer"
                    onClick={() => setFlash(null)}
                    aria-label="Close"
                  >
                    <span aria-hidden="true" className="bg-transparent">×</span>
                  </button>
                </div>
              )}

              {/* Error message */}
              {error && (
                <div className="
                  relative mb-4
                  pl-[60px] pr-12 py-[12px]
                  !bg-[#e84b3d] text-white
                  rounded
                  after:content-[''] after:absolute after:w-[57px] after:h-[52px]
                  after:bg-[url('/images/icons-set.png')] after:bg-no-repeat
                  after:top-[14px] after:left-[5px]
                  after:bg-[position:11px_-226px]
                " role="alert">
                  <strong className="block font-bold">{t('views.layouts.home.attention', 'Atenção!')}</strong>
                  <small className="text-[80%] font-normal">{error}</small>
                  <button
                    type="button"
                    className="absolute top-[8px] right-[4px] w-[36px] h-[36px] flex items-center justify-center rounded-full text-[20px] leading-none font-normal opacity-60 hover:opacity-90 bg-transparent border-0 cursor-pointer"
                    onClick={() => setError('')}
                    aria-label="Close"
                  >
                    <span aria-hidden="true" className="bg-transparent">×</span>
                  </button>
                </div>
              )}

              {/* Login form */}
              <form onSubmit={handleSubmit} role="form">
                {/* Email */}
                <div className={`${fieldWrapperClasses} mb-[12px]`}>
                  <input
                    type="email"
                    className={inputClasses}
                    placeholder={t('views.login.email_placeholder', 'E-mail')}
                    tabIndex={1}
                    autoFocus
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>

                {/* Password with eye toggle */}
                <div className={fieldWrapperClasses}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className={inputClasses}
                    placeholder={t('views.login.password_placeholder', 'Senha')}
                    tabIndex={2}
                    autoComplete="off"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                  <button
                    type="button"
                    className="
                      shrink-0 w-[44px] h-full
                      flex items-center justify-center
                      bg-transparent border-0
                      text-[16px] text-mist-500 hover:text-navy-700
                      cursor-pointer
                      focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-sky-500
                    "
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword
                      ? t('views.login.hide_password', 'Ocultar senha')
                      : t('views.login.show_password', 'Mostrar senha')}
                    aria-pressed={showPassword}
                  >
                    <i className={`fa ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                  </button>
                </div>

                {/* Recuperação de senha — junto do campo a que ela pertence,
                    alinhada à direita, na mesma ordem do app mobile */}
                <div className="mt-[8px] flex justify-end">
                  <Link
                    to="/recuperar-senha"
                    className="
                      inline-flex items-center
                      min-h-[44px] px-[2px] rounded-[6px]
                      text-[12px] !text-sky-200
                      underline decoration-sky-200/60 underline-offset-[3px]
                      hover:!text-white hover:decoration-white
                      font-['Open_Sans',sans-serif]
                      focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500
                      focus-visible:ring-offset-2 focus-visible:ring-offset-navy-800
                    "
                  >
                    {t('views.login.forgot_password', 'Esqueceu sua senha?')}
                  </Link>
                </div>

                {/* Submit button */}
                <button
                  type="submit"
                  className="
                    w-[100px] mx-auto block
                    min-h-[44px] px-[10px]
                    bg-lime-500 hover:bg-lime-400
                    !text-navy-900 text-[12px] font-semibold
                    border-0 rounded-full
                    cursor-pointer
                    transition-[background-color,transform] duration-150 ease-[cubic-bezier(0.23,1,0.32,1)]
                    active:scale-[0.97] motion-reduce:transform-none
                    focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500
                    focus-visible:ring-offset-2 focus-visible:ring-offset-navy-800
                    disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100
                    mt-[16px]
                  "
                  disabled={isSubmitting}
                >
                  {isSubmitting ? t('views.login.signing_in', 'Entrando...') : t('views.login.sign_in', 'Entrar')}
                </button>
              </form>

              {/* Separador: fecha o grupo de credenciais e abre o das alternativas */}
              <div className="mt-[24px] flex items-center gap-[12px]">
                <span aria-hidden="true" className="h-px flex-1 bg-white/15" />
                <span className="text-[11px] font-medium uppercase tracking-[0.08em] text-white/50">
                  {t('views.login.or', 'ou')}
                </span>
                <span aria-hidden="true" className="h-px flex-1 bg-white/15" />
              </div>

              {/* Social login options */}
              <div className="login-options mt-[20px] flex flex-col gap-[10px]">
                <SocialButton
                  icon="fa fa-facebook"
                  label={t('views.login.sign_in_with_facebook', 'Entrar com o Facebook')}
                  brandClasses="bg-[#3b5998] hover:bg-[#2d4373]"
                  onClick={() => submitOmniauthRequest('facebook').catch(() => oauthError('Facebook'))}
                />
                <SocialButton
                  icon="fa fa-google-plus"
                  label={t('views.login.sign_in_with_google', 'Entrar com o Google')}
                  brandClasses="bg-[#dd4b39] hover:bg-[#c23321]"
                  onClick={() => submitOmniauthRequest('google_oauth2').catch(() => oauthError('Google'))}
                />
                <SocialButton
                  icon="fa fa-apple"
                  label={t('views.login.sign_in_with_apple', 'Entrar com a Apple')}
                  brandClasses="bg-black hover:bg-[#333]"
                  onClick={() => submitOmniauthRequest('apple').catch(() => oauthError('Apple'))}
                />
              </div>

              {/* Criar conta (auto-cadastro) */}
              <div className="mt-[24px]">
                <Link
                  to="/primeiro-acesso"
                  className="
                    flex items-center justify-center gap-2
                    w-full min-h-[44px] px-[15px]
                    rounded-full border border-solid border-lime-500
                    !text-lime-500 text-[12px] font-medium bg-transparent
                    hover:bg-lime-500 hover:!text-navy-900
                    transition-colors duration-150
                    focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500
                    focus-visible:ring-offset-2 focus-visible:ring-offset-navy-800
                  "
                >
                  <i className="fa fa-user-plus" aria-hidden="true"></i>
                  {t('views.login.create_account', 'Criar conta')}
                </Link>
              </div>

              {/* Footer logo */}
              <div className="text-center mt-[40px] mb-[40px] min-[813px]:mt-[50px] min-[813px]:mb-[90px]">
                <img
                  src="/images/logo-footer.png"
                  alt="Laager"
                  width={79}
                  height={21}
                  className="mx-auto"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
